import Select from "react-select";
import styled from "styled-components";
import { useHistory } from "react-router";
import { allowDelete, allowSave, Option } from "../../store/helpers";
import { useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { RootState } from "../../store/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { UserAccess } from "../../interfaces/UserAccess";
import { PermissionMenuName } from "../../interfaces/UserRole";
import { fieldErrorMessage, fieldHasErrors } from "../../store/errorHandler";
import { getSelectedStatusOption, STATUS_OPTIONS } from "../../store/helpers";
import { createRoleAsync, getDetailRoleAsync, initializeCreateRole, saveRoleAsync, updateName, updatePermissions, updateStatus } from "../../reducers/userRole/userRole.actions";

export const UserRoleUpdatePage = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [isNew, setIsNew] = useState<boolean>(false);
  const errors = useSelector((state: RootState) => state.userRole.error);
  const isBusy = useSelector((state: RootState) => state.userRole.isLoading);
  const role = useSelector((state: RootState) => state.userRole.selectedRole);
  const redirectQueue = useSelector((state: RootState) => state.userRole.redirectQueue);
  const permission = useSelector((state: RootState) => state.authorization.systemAccess[PermissionMenuName.userRole].ACCESS);

  const canSave = allowSave(permission, 2);

  /**
   * If redirectQueue is provided, resets detail data and redirect user to the
   * list page.
   */
  useEffect(() => {
    if (redirectQueue) {
      history.push(redirectQueue);
      dispatch(initializeCreateRole());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectQueue]);

  useEffect(() => {
    const paths = location.pathname.split("/");
    if (paths[2] === 'create') {
      setIsNew(true);
      dispatch(initializeCreateRole());
    } else {
      setIsNew(false);
      const id = parseInt(paths[2]);
      if (id && !isNaN(id)) {
        dispatch(getDetailRoleAsync.request({ id }));
      } else {
        history.push(`/user-role/create`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const updatePermission = (menuIndex: number, key: 'create' | 'read' | 'x' | 'update' | 'delete', access: boolean) => {
    if (role) {
      const newPermissions: UserAccess[] = [...role.PERMISSION_DETAIL];
      newPermissions[menuIndex].ACCESS[key] = access;
      if (key === 'update' && !access) {
        newPermissions[menuIndex].ACCESS['delete'] = false;
      } else if (key === 'delete' && access) {
        newPermissions[menuIndex].ACCESS['update'] = true;
      } else if (key === 'read' && !access) {
        newPermissions[menuIndex].ACCESS['x'] = false;
        newPermissions[menuIndex].ACCESS['create'] = false;
        newPermissions[menuIndex].ACCESS['update'] = false;
        newPermissions[menuIndex].ACCESS['delete'] = false;
      }
      dispatch(updatePermissions(newPermissions));
    }
  }

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateName(e.target.value));
  }

  const onChangeStatus = (e: Option | null) => {
    if (!e) return;
    if (isNaN(parseInt(e.value))) return;
    dispatch(updateStatus(parseInt(e.value)));
  }

  const saveRole = () => {
    if (!role) return;

    if (isNew) {
      dispatch(createRoleAsync.request(role));
    } else if (role) {
      dispatch(saveRoleAsync.request(role));
    }
  }

  const requireReadX = (menuName: string) => {
    if ([
      PermissionMenuName.monevPartner.toString(),
      PermissionMenuName.dokumenKerjasama.toString()
    ].includes(menuName)) return true;
    return false
  }

  const disableDelete = (item: UserAccess) => item.MENU_NAME === PermissionMenuName.partner || item.MENU_NAME === PermissionMenuName.dokumenKerjasama || item.MENU_NAME === PermissionMenuName.monevPartner;

  const generateRoleChecklist = () => {
    if (role && role.PERMISSION_DETAIL) {
      return role.PERMISSION_DETAIL.map((item, index) => {
        return (
          <tr key={index}>
            <td>{item.MENU_NAME}</td>
            <td>
              <input
                onChange={() => updatePermission(index, 'read', !item.ACCESS.read)}
                checked={item.ACCESS.read}
                type="checkbox"
                disabled={isBusy || !canSave}
              />
            </td>
            <th>
              <input
                onChange={() => updatePermission(index, 'x', !item.ACCESS.x)}
                checked={requireReadX(item.MENU_NAME) ? item.ACCESS.x : item.ACCESS.read}
                type="checkbox"
                disabled={isBusy || !requireReadX(item.MENU_NAME) || !canSave}
              />
            </th>
            <th>
              <input
                onChange={() => updatePermission(index, 'create', !item.ACCESS.create)}
                checked={item.ACCESS.create}
                type="checkbox"
                disabled={isBusy || !canSave}
              />
            </th>
            <th>
              <input
                onChange={() => updatePermission(index, 'update', !item.ACCESS.update)}
                checked={item.ACCESS.update}
                type="checkbox"
                disabled={isBusy || !canSave}
              />
            </th>
            <th>
              <input
                type="checkbox"
                disabled={isBusy || disableDelete(item) || !canSave}
                onChange={() => updatePermission(index, 'delete', !item.ACCESS.delete)}
                checked={disableDelete(item) ? false : item.ACCESS.delete}
              />
            </th>
          </tr>
        );
      });      
    } else {
      return (
        <tr>
          <td colSpan={6}>
            Sedang memuat data &nbsp; <i className="fa fa-refresh fa-spin"></i>
          </td>
        </tr>
      )
    }
  }

  return (
    <UserRoleUpdatePageWrapper>
      <section className="content">
        <div className="row">
          <div className="col-lg-6">
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">{isNew ? 'Buat' : 'Ubah'} Peran</h3>
                <div className="box-tools">
                  
                </div>
              </div>
              <div className="box-body table-responsive no-padding">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Bagian</th>
                      <th>R</th>
                      <th>R+</th>
                      <th>C</th>
                      <th>U</th>
                      <th>D</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generateRoleChecklist()}
                  </tbody>
                </table>
              </div>
              <div className="box-body">
                <div className={`form-group ${fieldHasErrors('NAME', errors) ? 'has-error' : ''}`}>
                  <label>Nama Peran</label>
                  <input
                    type="text"
                    disabled={isBusy || !canSave}
                    onChange={onChangeName}
                    className='form-control'
                    defaultValue={role?.NAME}
                    placeholder="Masukan nama peran disini"
                  />
                  <span className="help-block">{ fieldErrorMessage('NAME', errors) }</span>
                </div>
                <div className={`form-group ${fieldHasErrors('STATUS', errors) ? 'has-error' : ''}`}>
                  <label>Status</label>
                  <Select 
                    options={STATUS_OPTIONS}
                    onChange={onChangeStatus}
                    value={getSelectedStatusOption(role?.STATUS)}
                    isDisabled={isBusy || !allowDelete(permission, 2)}
                  />
                  <span className="help-block">{ fieldErrorMessage('STATUS', errors) }</span>
                </div>
                <p style={{marginTop: '10px'}}>
                  <button
                    className="btn btn-primary"
                    style={{marginRight: '4px'}}
                    onClick={() => history.push('/user-role')}
                  >
                    kembali
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveRole || !canSave}
                    disabled={isBusy}
                  >
                    { isNew ? 'Buat Baru' : 'Simpan' }
                  </button>
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="box">
              <div className="box-header">
                <h3 className="box-title">Keterangan</h3>
              </div>
              <div className="box-body table-responsive no-padding">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Hak Akses</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <b>R / Read</b>
                      </td>
                      <td>
                        Pengguna hanya dapat melihat data yang dibuat oleh dirinya
                        sendiri.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>R+ / Read Plus</b>
                      </td>
                      <td>
                        Pengguna dapat melihat semua data termasuk pengguna
                        lainnya. Hanya berlaku pada menu dokumen kerjasama dan monev.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>C / Create</b>
                      </td>
                      <td>Pengguna dapat membuat data baru.</td>
                    </tr>
                    <tr>
                      <td>
                        <b>U / Update</b>
                      </td>
                      <td>Pengguna dapat memperbaharui data.</td>
                    </tr>
                    <tr>
                      <td>
                        <b>D / Delete</b>
                      </td>
                      <td>
                        Pengguna dapat menonaktifkan data dengan mengubah status.
                        <ul>
                          <li>Hanya dapat digunakan pada master data, user control, dan user role.</li>
                          <li>Agar dapat mengubah status, pengguna juga harus memiliki hak akses untuk memperbaharui data.</li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </UserRoleUpdatePageWrapper>
  );
};

const UserRoleUpdatePageWrapper = styled.div``;
